/**
 * @fileoverview ClubOS — application routes
 * Routes include a `permission` field for RBAC enforcement in ProtectedRoute.
 * Auth routes (sign-in, sign-up) are identified by their path prefix.
 */

import Icon from "@mui/material/Icon";

// Authentication
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";

// Profile
import Profile from "layouts/profile";

// Role Dashboards
import ExecutiveDashboard from "layouts/dashboards/executive";
import FinanceDashboard    from "layouts/dashboards/finance";
import CoachingDashboard   from "layouts/dashboards/coaching";
import MedicalDashboard    from "layouts/dashboards/medical";
import ScoutingDashboard   from "layouts/dashboards/scouting";

// Alerts
import Alerts from "layouts/alerts";

// Finance
import FinanceOverview      from "layouts/finance/overview";
import Contracts            from "layouts/finance/contracts";
import Transfers            from "layouts/finance/transfers";
import BudgetPage           from "layouts/finance/budget";
import RevenuePage          from "layouts/finance/revenue";
import StaffContractsPage   from "layouts/finance/staff-contracts";
import SponsorshipsPage     from "layouts/finance/sponsorships";
import MatchdayRevenuePage  from "layouts/finance/matchday";
import ExpensesPage         from "layouts/finance/expenses";

// Squad
import SquadManagement from "layouts/squad/management";
import PlayerProfiles  from "layouts/squad/players";
import Registrations   from "layouts/squad/registrations";

// Coaching
import Tactics   from "layouts/coaching/tactics";
import Training  from "layouts/coaching/training";
import MatchLog  from "layouts/coaching/matches";

// Scouting
import ScoutingPipeline  from "layouts/scouting/pipeline";
import ScoutReports      from "layouts/scouting/reports";
import PlayerComparison  from "layouts/scouting/compare";

// Youth Academy
import AcademyDashboard from "layouts/academy/dashboard";
import AcademyPlayers   from "layouts/academy/players";
import AcademyBatches   from "layouts/academy/batches";
import AcademyCoaches   from "layouts/academy/coaches";
import AcademyAttendance from "layouts/academy/attendance";
import AcademyFees      from "layouts/academy/fees";
import AcademyExpenses  from "layouts/academy/expenses";
import AcademyRatings   from "layouts/academy/ratings";
// Users
import UsersPage     from "layouts/users";
import RolesPage     from "layouts/users/roles";
import AcademyConfig from "layouts/academy/config";
// Admin
import AuditLogs from "layouts/admin/audit-logs";

// Settings
import Settings from "layouts/settings";

const routes = [
  // ── Role Dashboards ────────────────────────────────────────────────────────
  { type: "title", title: "Dashboards", key: "title-dashboards" },
  {
    type: "collapse",
    name: "Executive",
    key: "dashboard-executive",
    route: "/dashboards/executive",
    icon: <Icon fontSize="small">star</Icon>,
    component: <ExecutiveDashboard />,
    // All authenticated users see the executive overview
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Finance",
    key: "dashboard-finance",
    route: "/dashboards/finance",
    icon: <Icon fontSize="small">account_balance</Icon>,
    component: <FinanceDashboard />,
    permission: "finance.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Coaching & Players",
    key: "dashboard-coaching",
    route: "/dashboards/coaching",
    icon: <Icon fontSize="small">sports_soccer</Icon>,
    component: <CoachingDashboard />,
    permission: "coaching.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Medical",
    key: "dashboard-medical",
    route: "/dashboards/medical",
    icon: <Icon fontSize="small">local_hospital</Icon>,
    component: <MedicalDashboard />,
    permission: "squad.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Scouting",
    key: "dashboard-scouting",
    route: "/dashboards/scouting",
    icon: <Icon fontSize="small">radar</Icon>,
    component: <ScoutingDashboard />,
    permission: "scouting.view",
    noCollapse: true,
  },

  // ── Alerts ─────────────────────────────────────────────────────────────────
  {
    type: "collapse",
    name: "Alerts",
    key: "alerts",
    route: "/alerts",
    icon: <Icon fontSize="small">notifications</Icon>,
    component: <Alerts />,
    noCollapse: true,
  },

  // ── Finance section ────────────────────────────────────────────────────────
  { type: "title", title: "Finance", key: "title-finance" },
  {
    type: "collapse",
    name: "Overview",
    key: "finance-overview",
    route: "/finance/overview",
    icon: <Icon fontSize="small">account_balance</Icon>,
    component: <FinanceOverview />,
    permission: "finance.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Contracts",
    key: "finance-contracts",
    route: "/finance/contracts",
    icon: <Icon fontSize="small">description</Icon>,
    component: <Contracts />,
    permission: "finance.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Transfers",
    key: "finance-transfers",
    route: "/finance/transfers",
    icon: <Icon fontSize="small">swap_horiz</Icon>,
    component: <Transfers />,
    permission: "finance.transfer.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Budget",
    key: "finance-budget",
    route: "/finance/budget",
    icon: <Icon fontSize="small">pie_chart</Icon>,
    component: <BudgetPage />,
    permission: "finance.budget.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Revenue",
    key: "finance-revenue",
    route: "/finance/revenue",
    icon: <Icon fontSize="small">trending_up</Icon>,
    component: <RevenuePage />,
    permission: "finance.revenue.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Sponsorships",
    key: "finance-sponsorships",
    route: "/finance/sponsorships",
    icon: <Icon fontSize="small">handshake</Icon>,
    component: <SponsorshipsPage />,
    permission: "finance.revenue.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Matchday Revenue",
    key: "finance-matchday",
    route: "/finance/matchday",
    icon: <Icon fontSize="small">stadium</Icon>,
    component: <MatchdayRevenuePage />,
    permission: "finance.revenue.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Staff Contracts",
    key: "finance-staff-contracts",
    route: "/finance/staff-contracts",
    icon: <Icon fontSize="small">badge</Icon>,
    component: <StaffContractsPage />,
    permission: "finance.salary.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Expenses",
    key: "finance-expenses",
    route: "/finance/expenses",
    icon: <Icon fontSize="small">receipt_long</Icon>,
    component: <ExpensesPage />,
    permission: "finance.view",
    noCollapse: true,
  },

  // ── Squad section ──────────────────────────────────────────────────────────
  { type: "title", title: "Squad", key: "title-squad" },
  {
    type: "collapse",
    name: "Squad Management",
    key: "squad-management",
    route: "/squad/management",
    icon: <Icon fontSize="small">groups</Icon>,
    component: <SquadManagement />,
    permission: "squad.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Player Profiles",
    key: "squad-players",
    route: "/squad/players",
    icon: <Icon fontSize="small">person</Icon>,
    component: <PlayerProfiles />,
    permission: "squad.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Registrations",
    key: "squad-registrations",
    route: "/squad/registrations",
    icon: <Icon fontSize="small">how_to_reg</Icon>,
    component: <Registrations />,
    permission: "squad.view",
    noCollapse: true,
  },

  // ── Coaching section ───────────────────────────────────────────────────────
  { type: "title", title: "Coaching", key: "title-coaching" },
  {
    type: "collapse",
    name: "Tactics",
    key: "coaching-tactics",
    route: "/coaching/tactics",
    icon: <Icon fontSize="small">sports_soccer</Icon>,
    component: <Tactics />,
    permission: "coaching.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Training",
    key: "coaching-training",
    route: "/coaching/training",
    icon: <Icon fontSize="small">fitness_center</Icon>,
    component: <Training />,
    permission: "coaching.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Match Log",
    key: "coaching-matches",
    route: "/coaching/matches",
    icon: <Icon fontSize="small">emoji_events</Icon>,
    component: <MatchLog />,
    permission: "coaching.view",
    noCollapse: true,
  },

  // ── Scouting section ───────────────────────────────────────────────────────
  { type: "title", title: "Scouting", key: "title-scouting" },
  {
    type: "collapse",
    name: "Pipeline",
    key: "scouting-pipeline",
    route: "/scouting/pipeline",
    icon: <Icon fontSize="small">view_kanban</Icon>,
    component: <ScoutingPipeline />,
    permission: "scouting.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Scout Reports",
    key: "scouting-reports",
    route: "/scouting/reports",
    icon: <Icon fontSize="small">assignment</Icon>,
    component: <ScoutReports />,
    permission: "scouting.view",
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Player Comparison",
    key: "scouting-compare",
    route: "/scouting/compare",
    icon: <Icon fontSize="small">compare_arrows</Icon>,
    component: <PlayerComparison />,
    permission: "scouting.view",
    noCollapse: true,
  },

  // ── Youth Academy section ──────────────────────────────────────────────────
  { type: "title", title: "Youth Academy", key: "title-academy" },
  {
    type: "collapse",
    name: "Academy Overview",
    key: "academy-dashboard",
    route: "/academy/dashboard",
    icon: <Icon fontSize="small">school</Icon>,
    component: <AcademyDashboard />,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Players",
    key: "academy-players",
    route: "/academy/players",
    icon: <Icon fontSize="small">groups</Icon>,
    component: <AcademyPlayers />,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Batches",
    key: "academy-batches",
    route: "/academy/batches",
    icon: <Icon fontSize="small">category</Icon>,
    component: <AcademyBatches />,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Coaches",
    key: "academy-coaches",
    route: "/academy/coaches",
    icon: <Icon fontSize="small">sports_soccer</Icon>,
    component: <AcademyCoaches />,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Attendance",
    key: "academy-attendance",
    route: "/academy/attendance",
    icon: <Icon fontSize="small">fact_check</Icon>,
    component: <AcademyAttendance />,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Fees",
    key: "academy-fees",
    route: "/academy/fees",
    icon: <Icon fontSize="small">receipt</Icon>,
    component: <AcademyFees />,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Expenses",
    key: "academy-expenses",
    route: "/academy/expenses",
    icon: <Icon fontSize="small">receipt_long</Icon>,
    component: <AcademyExpenses />,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Player Ratings",
    key: "academy-ratings",
    route: "/academy/ratings",
    icon: <Icon fontSize="small">emoji_emotions</Icon>,
    component: <AcademyRatings />,
    noCollapse: true,
  },
  // ── Users section ──────────────────────────────────────────────────────────
  { type: "title", title: "Administration", key: "title-admin" },
  {
    type: "collapse",
    name: "Users",
    key: "users",
    route: "/users",
    icon: <Icon fontSize="small">manage_accounts</Icon>,
    component: <UsersPage />,
    permission: "users.view",
    adminOnly: true,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Roles & Permissions",
    key: "users-roles",
    route: "/users/roles",
    icon: <Icon fontSize="small">admin_panel_settings</Icon>,
    component: <RolesPage />,
    permission: "users.view",
    adminOnly: true,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Academy Config",
    key: "academy-config",
    route: "/academy/config",
    icon: <Icon fontSize="small">tune</Icon>,
    component: <AcademyConfig />,
    permission: "users.view",
    adminOnly: true,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Edit History",
    key: "audit-logs",
    route: "/admin/audit-logs",
    icon: <Icon fontSize="small">manage_search</Icon>,
    component: <AuditLogs />,
    permission: "users.view",
    adminOnly: true,
    noCollapse: true,
  },

  // ── Settings ───────────────────────────────────────────────────────────────
  { type: "divider", key: "divider-settings" },
  {
    type: "collapse",
    name: "Settings",
    key: "settings",
    route: "/settings",
    icon: <Icon fontSize="small">settings</Icon>,
    component: <Settings />,
    noCollapse: true,
  },

  // ── Hidden routes (not in sidenav) ─────────────────────────────────────────
  {
    key: "profile",
    route: "/profile",
    component: <Profile />,
    // No permission — any authenticated user can view their own profile
  },
  {
    key: "sign-in",
    route: "/authentication/sign-in",
    component: <SignIn />,
  },
  {
    key: "sign-up",
    route: "/authentication/sign-up",
    component: <SignUp />,
  },
];

export default routes;
